import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { BrandLoader } from "../components/site/Logo";
import AdminLayout from "./AdminLayout";
import { Login, Setup, ForgotPassword, ResetPassword, AcceptInvite } from "./AuthPages";
import Dashboard from "./Dashboard";
import { ArticlesList, Workflow } from "./Articles";
import ArticleEditor from "./ArticleEditor";
import Categories from "./Categories";
import Media from "./Media";
import Users from "./Users";
import Inquiries from "./Inquiries";
import Settings from "./Settings";
import AuditLogs from "./AuditLogs";
import Profile from "./Profile";
import ComingSoon from "./ComingSoon";
import Calendar from "./Calendar";
import ArticlePreview from "./ArticlePreview";
import { FactCheckQueue, FactCheckWorkspace } from "./FactCheck";
import Homepage from "./Homepage";
import AiInbox from "./AiInbox";
import { NrDashboard, NrQueue, NrCluster, NrGenerator, NrErrors, NrSettings } from "./AiNewsroom";
import LegalKb from "./LegalKb";
import LaunchContent from "./LaunchContent";
import GuideQuality from "./GuideQuality";
import { NrSources } from "./SourceRegistry";
import { Forbidden } from "./ui";

const ANALYTICS_PERMS = ["analytics.view", "analytics.own", "analytics.marketing", "analytics.editorial", "analytics.admin", "analytics.reports"];
import Personalization from "./Personalization";
import AnalyticsCenter from "./AnalyticsCenter";
import Distribution from "./Distribution";
import Partners from "./Partners";
import Sales from "./Sales";
import Quotes from "./Quotes";
import Finance from "./Finance";
import Commercial from "./Commercial";
import BalkanAiAdmin from "./BalkanAi";
import ToolsAdmin from "./ToolsAdmin";
import ToolChecks from "./ToolChecks";
import FormsAdmin from "./FormsAdmin";
import Wissenszentrum from "./Wissenszentrum";
import CategoryDrafts from "./CategoryDrafts";
import PremiumAdmin from "./PremiumAdmin";
import Korpa from "./Korpa";
import EmailCenter from "./EmailCenter";
import EmailTemplates from "./EmailTemplates";
import Marketing from "./Marketing";
import { ReadersList, ReaderDetail } from "./Readers";
import LegalAdmin from "./LegalAdmin";
import Corrections from "./Corrections";
import Cancellations from "./Cancellations";
import PrivacyCenter from "./PrivacyCenter";
import FooterAdmin from "./FooterAdmin";
import AdsAdmin from "./AdsAdmin";

const Protected = ({ children }) => {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <BrandLoader label="Provjera sesije…" />;
  if (!user) return <Navigate to="/admin/login" replace state={{ from: loc.pathname }} />;
  return children;
};

const Gate = ({ perms, children }) => {
  const { can } = useAuth();
  return can(...perms) ? children : <Forbidden />;
};

export default function AdminRoutes() {
  return (
    <Routes>
      <Route path="login" element={<Login />} />
      <Route path="setup" element={<Setup />} />
      <Route path="forgot-password" element={<ForgotPassword />} />
      <Route path="reset-password" element={<ResetPassword />} />
      <Route path="accept-invite" element={<AcceptInvite />} />
      <Route path="pregled/:id" element={<Protected><ArticlePreview /></Protected>} />
      <Route element={<Protected><AdminLayout /></Protected>}>
        <Route path="kalendar" element={<Gate perms={["articles.create", "articles.view_all"]}><Calendar /></Gate>} />
        <Route path="provjera" element={<Gate perms={["articles.factcheck"]}><FactCheckQueue /></Gate>} />
        <Route path="provjera/:id" element={<Gate perms={["articles.factcheck"]}><FactCheckWorkspace /></Gate>} />
        <Route path="ai-redakcija" element={<Gate perms={["newsroom.view"]}><NrDashboard /></Gate>} />
        <Route path="ai-redakcija/za-pregled" element={<Gate perms={["newsroom.view", "articles.create", "articles.view_all"]}><AiInbox /></Gate>} />
        <Route path="ai-redakcija/red" element={<Gate perms={["newsroom.view"]}><NrQueue /></Gate>} />
        <Route path="ai-redakcija/prica/:id" element={<Gate perms={["newsroom.view"]}><NrCluster /></Gate>} />
        <Route path="ai-redakcija/generator" element={<Gate perms={["newsroom.view"]}><NrGenerator /></Gate>} />
        <Route path="ai-redakcija/izvori" element={<Gate perms={["newsroom.view"]}><NrSources /></Gate>} />
        <Route path="ai-redakcija/greske" element={<Gate perms={["newsroom.view"]}><NrErrors /></Gate>} />
        <Route path="ai-redakcija/postavke" element={<Gate perms={["newsroom.view"]}><NrSettings /></Gate>} />
        <Route path="ai-redakcija/pravna-baza" element={<Gate perms={["newsroom.view"]}><LegalKb /></Gate>} />
        <Route path="lansiranje" element={<Gate perms={["articles.publish"]}><LaunchContent /></Gate>} />
        <Route path="vodici/kvalitet" element={<Gate perms={["tools.view"]}><GuideQuality /></Gate>} />
        <Route path="distribucija" element={<Gate perms={["distribution.view"]}><Distribution /></Gate>} />
        <Route path="distribucija/kampanje/:id" element={<Gate perms={["distribution.view"]}><Distribution /></Gate>} />
        <Route path="distribucija/:section" element={<Gate perms={["distribution.view"]}><Distribution /></Gate>} />
        <Route path="partneri" element={<Gate perms={["partners.view"]}><Partners /></Gate>} />
        <Route path="partneri/:id" element={<Gate perms={["partners.view"]}><Partners /></Gate>} />
        <Route path="prodaja" element={<Gate perms={["sales.view"]}><Sales /></Gate>} />
        <Route path="prodaja/:id" element={<Gate perms={["sales.view"]}><Sales /></Gate>} />
        <Route path="ponude" element={<Gate perms={["sales.view", "finance.view"]}><Quotes /></Gate>} />
        <Route path="ponude/:id" element={<Gate perms={["sales.view", "finance.view"]}><Quotes /></Gate>} />
        <Route path="oglasavanje" element={<Gate perms={["ads.view"]}><AdsAdmin /></Gate>} />
        <Route path="oglasavanje/:section" element={<Gate perms={["ads.view"]}><AdsAdmin /></Gate>} />
        <Route path="oglasavanje/:section/:id" element={<Gate perms={["ads.view"]}><AdsAdmin /></Gate>} />
        <Route path="balkan-ai" element={<Gate perms={["ai.view"]}><BalkanAiAdmin /></Gate>} />
        <Route path="balkan-ai/:section" element={<Gate perms={["ai.view"]}><BalkanAiAdmin /></Gate>} />
        <Route path="alati" element={<Gate perms={["tools.view"]}><ToolsAdmin /></Gate>} />
        <Route path="alati/provjere" element={<Gate perms={["tools.view"]}><ToolChecks /></Gate>} />
        <Route path="formulari" element={<Gate perms={["tools.view"]}><FormsAdmin /></Gate>} />
        <Route path="wissenszentrum" element={<Gate perms={["tools.view"]}><Wissenszentrum /></Gate>} />
        <Route path="ai-redakcija/kategorije" element={<Gate perms={["newsroom.view"]}><CategoryDrafts /></Gate>} />
        <Route path="premium" element={<Gate perms={["premium.view"]}><PremiumAdmin /></Gate>} />
        <Route path="premium/:section" element={<Gate perms={["premium.view"]}><PremiumAdmin /></Gate>} />
        <Route path="korpa" element={<Gate perms={["records.manage"]}><Korpa /></Gate>} />
        <Route path="email-centrum" element={<Gate perms={["email.view"]}><EmailCenter /></Gate>} />
        <Route path="email-centrum/:id" element={<Gate perms={["email.view"]}><EmailCenter /></Gate>} />
        <Route path="email-vorlagen" element={<Gate perms={["email.view"]}><EmailTemplates /></Gate>} />
        <Route path="email-vorlagen/:key" element={<Gate perms={["email.view"]}><EmailTemplates /></Gate>} />
        <Route path="marketing" element={<Gate perms={["sales.view"]}><Marketing /></Gate>} />
        <Route path="marketing/:id" element={<Gate perms={["sales.view"]}><Marketing /></Gate>} />
        <Route path="korisnici" element={<Gate perms={["readers.view"]}><ReadersList /></Gate>} />
        <Route path="korisnici/:id" element={<Gate perms={["readers.view"]}><ReaderDetail /></Gate>} />
        <Route path="rechtliches" element={<Gate perms={["legal.view"]}><LegalAdmin /></Gate>} />
        <Route path="rechtliches/:slug" element={<Gate perms={["legal.view"]}><LegalAdmin /></Gate>} />
        <Route path="komercijala" element={<Gate perms={["sales.view", "ads.view", "finance.view"]}><Commercial /></Gate>} />
        <Route path="komercijala/:section" element={<Gate perms={["sales.view", "ads.view", "finance.view", "pricing.manage"]}><Commercial /></Gate>} />
        <Route path="finansije" element={<Gate perms={["finance.view"]}><Finance /></Gate>} />
        <Route path="finansije/:section" element={<Gate perms={["finance.view"]}><Finance /></Gate>} />
        <Route path="finansije/:section/:id" element={<Gate perms={["finance.view"]}><Finance /></Gate>} />
        <Route path="analitika" element={<Gate perms={ANALYTICS_PERMS}><AnalyticsCenter /></Gate>} />
        <Route path="analitika/clanci/:id" element={<Gate perms={ANALYTICS_PERMS}><AnalyticsCenter /></Gate>} />
        <Route path="analitika/:section" element={<Gate perms={ANALYTICS_PERMS}><AnalyticsCenter /></Gate>} />
        <Route path="personalizacija" element={<Gate perms={["personalization.view"]}><Personalization /></Gate>} />
        <Route path="naslovnica" element={<Gate perms={["articles.publish"]}><Homepage /></Gate>} />
        <Route index element={<Gate perms={["dashboard.view"]}><Dashboard /></Gate>} />
        <Route path="articles" element={<Gate perms={["articles.create", "articles.view_all", "articles.factcheck"]}><ArticlesList /></Gate>} />
        <Route path="articles/new" element={<Gate perms={["articles.create"]}><ArticleEditor /></Gate>} />
        <Route path="articles/:id" element={<Gate perms={["articles.create", "articles.view_all", "articles.factcheck"]}><ArticleEditor /></Gate>} />
        <Route path="workflow" element={<Gate perms={["articles.create", "articles.view_all", "articles.factcheck"]}><Workflow /></Gate>} />
        <Route path="categories" element={<Gate perms={["dashboard.view"]}><Categories /></Gate>} />
        <Route path="media" element={<Gate perms={["media.upload", "media.manage"]}><Media /></Gate>} />
        <Route path="users" element={<Gate perms={["users.view", "users.manage"]}><Users /></Gate>} />
        <Route path="inquiries" element={<Gate perms={["contact.view"]}><Inquiries /></Gate>} />
        <Route path="ispravke" element={<Gate perms={["corrections.view"]}><Corrections /></Gate>} />
        <Route path="otkazi" element={<Gate perms={["cancellations.view"]}><Cancellations /></Gate>} />
        <Route path="datenschutz" element={<Gate perms={["privacy.view"]}><PrivacyCenter /></Gate>} />
        <Route path="datenschutz/:tab" element={<Gate perms={["privacy.view"]}><PrivacyCenter /></Gate>} />
        <Route path="postavke/footer" element={<Gate perms={["settings.manage"]}><FooterAdmin /></Gate>} />
        <Route path="settings" element={<Gate perms={["settings.manage"]}><Settings /></Gate>} />
        <Route path="audit" element={<Gate perms={["audit.view"]}><AuditLogs /></Gate>} />
        <Route path="profile" element={<Profile />} />
        <Route path="soon/:module" element={<ComingSoon />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Route>
    </Routes>
  );
}
